import React, { useEffect, useRef, useState } from "react";
import type { FeedbackCapture, FeedbackListInput, FeedbackListResponse, FeedbackRequestDetail, FeedbackRequestSummary, FeedbackScope, FeedbackStatus } from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { errorText, when } from "../format.js";
import { Icon } from "../icons.js";
import { Link } from "../router.js";
import { useLoad, useSession } from "../session.js";
import { Callout, Chip, Crumb, Head, useTitle } from "../ui.js";
import { useOpenRequest } from "./RequestComposer.js";

const STATUS: Record<FeedbackStatus, { label: string; kind: "" | "ok" | "warn" | "stop" | "quiet"; text: string }> = {
  submitted: { label: "Awaiting Phoneware", kind: "warn", text: "Submitted. Waiting for Phoneware staff to start agent work." },
  filing: { label: "Filing", kind: "quiet", text: "Filing the request. The submitted text remains here." },
  filed: { label: "Filed", kind: "quiet", text: "Filed. Waiting for agent work." },
  filing_failed: { label: "Filing failed", kind: "stop", text: "Filing failed. The request is still saved. Phoneware can retry filing." },
  agent_working: { label: "Agent working", kind: "", text: "Agent working. Progress is recorded below." },
  agent_failed: { label: "Agent failed", kind: "stop", text: "Agent failed. Phoneware can retry without losing the earlier attempt." },
  change_ready: { label: "Draft PR ready", kind: "", text: "Draft PR ready for review. The agent cannot merge or deploy." },
  replied: { label: "Reply", kind: "ok", text: "Agent replied without a code change." },
  merged: { label: "Merged", kind: "quiet", text: "Merged. Awaiting deployment verification." },
  deploying: { label: "Deploying", kind: "", text: "Deploying. Not verified live yet." },
  live: { label: "Live, verified", kind: "ok", text: "Live, verified against the serving revision." },
  closed: { label: "Closed", kind: "quiet", text: "This request is closed. Its history remains visible." },
};
const ACTIVE: FeedbackStatus[] = ["submitted", "filing", "filed", "filing_failed", "agent_working", "agent_failed", "change_ready", "replied", "merged", "deploying"];
const FILTERS: FeedbackStatus[] = ["submitted", "filing", "filed", "filing_failed", "agent_working", "agent_failed", "change_ready", "replied", "merged", "deploying", "live", "closed"];

function requestTitle(comment: string): string { return comment.split("\n")[0].slice(0, 90); }
function scopeName(scope: FeedbackScope, names: Map<string, string>): string {
  return scope.kind === "platform" ? "Platform" : names.get(scope.dealerId) ?? scope.dealerId;
}
function StatusChip({ status }: { status: FeedbackStatus }): React.ReactElement {
  return <Chip kind={STATUS[status].kind} status={status}>{STATUS[status].label}</Chip>;
}

export function Requests(): React.ReactElement {
  useTitle("Requests");
  const { api, me } = useSession();
  const openRequest = useOpenRequest();
  const [filter, setFilter] = useState<FeedbackStatus | "" | "mine">("");
  const [scopeValue, setScopeValue] = useState("");
  const [more, setMore] = useState<{ key: string; items: FeedbackRequestSummary[] }>({ key: "", items: [] });
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [paging, setPaging] = useState(false);
  const [pageError, setPageError] = useState("");
  const viewKey = `${filter}:${scopeValue}`;
  const currentView = useRef(viewKey);
  currentView.current = viewKey;
  const scope: FeedbackScope | undefined = scopeValue === "platform" ? { kind: "platform" } : scopeValue.startsWith("dealer:") ? { kind: "dealer", dealerId: scopeValue.slice(7) } : undefined;
  const input: FeedbackListInput = { ...(scope ? { scope } : {}), ...(filter === "mine" ? { mine: true } : filter ? { status: filter } : {}) };
  const first = useLoad<{ key: string; response: FeedbackListResponse }>(
    () => api.feedbackRequests(input).then((response) => ({ key: viewKey, response })),
    [api, filter, scopeValue],
  );
  const currentPage = first.data?.key === viewKey ? first.data.response : null;
  const chooseScope = first.data?.response.capabilities.chooseScope ?? false;
  const dealers = useLoad(() => chooseScope ? api.dealers() : Promise.resolve([]), [api, chooseScope]);
  const names = new Map((dealers.data ?? []).map((dealer) => [dealer.id, dealer.name]));
  const moreItems = more.key === viewKey ? more.items : [];
  const rows = [...(currentPage?.items ?? []), ...moreItems.filter((item) => !currentPage?.items.some((firstItem) => firstItem.id === item.id))];
  useEffect(() => { setMore({ key: viewKey, items: [] }); setCursor(undefined); setPageError(""); }, [filter, scopeValue]);
  useEffect(() => {
    if (!currentPage?.items.some((item) => ACTIVE.includes(item.status))) return;
    const timer = window.setTimeout(() => first.reload(), 5000);
    return () => window.clearTimeout(timer);
  }, [first.data]);
  const next = currentPage ? cursor === undefined ? currentPage.nextCursor : cursor : null;
  const loadMore = async (): Promise<void> => {
    if (!next || paging) return;
    const loadingView = viewKey;
    setPaging(true); setPageError("");
    try {
      const page = await api.feedbackRequests({ ...input, cursor: next });
      if (currentView.current !== loadingView) return;
      setMore((existing) => {
        const items = existing.key === loadingView ? existing.items : [];
        return { key: loadingView, items: [...items, ...page.items.filter((item) => !items.some((old) => old.id === item.id))] };
      });
      setCursor(page.nextCursor);
    } catch (error) {
      if (currentView.current === loadingView) setPageError(error instanceof Error ? error.message : String(error));
    } finally { setPaging(false); }
  };
  return <>
    <Head title="Requests" sub={chooseScope ? "Request a change to Omni. Support and Super admins start agent work." : "Request a change to Omni. Phoneware staff start agent work."}
      actions={currentPage?.capabilities.create ? <button className="btn primary" type="button" onClick={openRequest}><Icon name="plus" />Request a change</button> : null} />
    <div className="filters request-filters">
      <label className="field"><span>Status</span><select className="input" value={filter} onChange={(event) => setFilter(event.target.value as FeedbackStatus | "" | "mine")} aria-label="Status">
        <option value="">All</option><option value="mine">Mine</option>{FILTERS.map((status) => <option value={status} key={status}>{STATUS[status].label}</option>)}
      </select></label>
      {chooseScope ? <label className="field"><span>Scope</span><select className="input" value={scopeValue} onChange={(event) => setScopeValue(event.target.value)} aria-label="Scope">
        <option value="">All</option><option value="platform">Platform</option>{(dealers.data ?? []).map((dealer) => <option key={dealer.id} value={`dealer:${dealer.id}`}>{dealer.name}</option>)}
      </select></label> : me.dealer ? <span className="scope-note">Only your requests at {me.dealer.name}</span> : null}
    </div>
    {first.error ? <Callout kind="stop" icon="triangle-alert">{first.error.message} <button className="link" type="button" onClick={first.reload}>Try again</button></Callout> : null}
    {dealers.error ? <Callout kind="stop" icon="triangle-alert">{dealers.error.message}</Callout> : null}
    <section className="card request-list"><div className="card-h"><h2>Requests</h2>{currentPage ? <span className="more">{rows.length} {rows.length === 1 ? "request" : "requests"}</span> : null}</div>
      {!currentPage ? <div className="empty" role="status">Loading requests.</div> : !rows.length ? <div className="empty">No requests.</div> :
        <ul className="rows">{rows.map((request) => <li key={request.id}><Link className="row" to={`/requests/${encodeURIComponent(request.id)}`} data-testid={`request-row-${request.id}`}>
          <span className="row-main"><b>{requestTitle(request.comment)}</b><span className="row-m">{request.id} · {scopeName(request.scope, names)} · {request.createdBy.name}</span></span>
          <span className="row-end"><StatusChip status={request.status} /><Icon name="chevron-right" /></span>
        </Link></li>)}</ul>}
      {next ? <button className="btn request-more" type="button" disabled={paging} onClick={() => { void loadMore(); }}>Load more</button> : null}
      {pageError ? <p role="alert" className="form-error">{pageError}</p> : null}
    </section>
  </>;
}

/**
 * The request's screens in the order they were saved. One keeps the familiar annotated screenshot; more become a
 * gallery whose chips scroll inside the card. Only the screen on show is fetched, and each one only once.
 */
function Screens({ id, captures }: { id: string; captures: FeedbackCapture[] }): React.ReactElement {
  const { api } = useSession();
  const [index, setIndex] = useState(0);
  const [, setFetched] = useState(0);
  const [failure, setFailure] = useState<{ captureId: string; message: string } | null>(null);
  const urls = useRef(new Map<string, string>());
  const asked = useRef(new Set<string>());
  const mounted = useRef(true);
  const position = Math.min(index, captures.length - 1);
  const shown = captures[position];
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      for (const url of urls.current.values()) URL.revokeObjectURL(url);
      urls.current.clear();
    };
  }, []);
  useEffect(() => {
    const captureId = shown.id;
    if (!shown.available || urls.current.has(captureId) || asked.current.has(captureId)) return;
    asked.current.add(captureId);
    api.feedbackCapture(id, captureId).then((blob) => {
      if (!mounted.current) return;
      urls.current.set(captureId, URL.createObjectURL(blob));
      setFailure((current) => (current?.captureId === captureId ? null : current));
      setFetched((n) => n + 1);
    }, (error: unknown) => {
      asked.current.delete(captureId);
      if (mounted.current) setFailure({ captureId, message: errorText(error) });
    });
  }, [api, id, shown.id, shown.available]);
  const count = captures.length;
  const url = urls.current.get(shown.id);
  const alt = count === 1 ? "Screenshot with annotations" : `Screen ${position + 1} of ${count}: ${shown.page.path}`;
  return <div className="request-snapshot">
    <div className="card-h"><h2>{count === 1 ? "Screenshot with annotations" : `Screens (${count})`}</h2></div>
    {count > 1 ? <div className="request-screens-chips" role="group" aria-label="Screens">
      {captures.map((capture, i) => <button key={capture.id} className="btn small" type="button" aria-pressed={i === position} aria-label={`Show screen ${i + 1}`}
        onClick={() => setIndex(i)}>Screen {i + 1}</button>)}
    </div> : null}
    {!shown.available ? <p>Screenshot unavailable.</p>
      : failure?.captureId === shown.id ? <p role="alert" className="form-error">{failure.message}</p>
      : url ? <img src={url} alt={alt} /> : <p role="status">Loading screenshot.</p>}
    <p className="request-screens-meta">Page {shown.page.path}{shown.page.selectedElement ? ` \u00b7 ${shown.page.selectedElement.tagName} selected` : ""}</p>
    {shown.note ? <p className="request-screens-note"><b>Note</b>{shown.note}</p> : null}
    <p className="scope-note">Screenshot expiry: <time dateTime={shown.expiresAt}>{new Date(shown.expiresAt).toLocaleString()}</time>. Request text and history remain afterward.</p>
  </div>;
}

export function RequestDetail({ id }: { id: string }): React.ReactElement {
  const { api, me } = useSession();
  const record = useLoad<FeedbackRequestDetail>(() => api.feedbackRequest(id), [api, id]);
  const [comment, setComment] = useState("");
  const [steer, setSteer] = useState(false);
  const [commentId, setCommentId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const data = record.data;
  useTitle(data ? requestTitle(data.comment) : "Request");
  useEffect(() => {
    if (!data || (!ACTIVE.includes(data.status) && data.status !== "live")) return;
    // A live request can cease to be live after a rollback. Keep reading the server,
    // but less often than an active agent attempt.
    const timer = window.setTimeout(() => record.reload(), data.status === "live" ? 30000 : 5000);
    return () => window.clearTimeout(timer);
  }, [data]);
  const command = async (action: "start" | "retry-filing" | "retry-run" | "close"): Promise<void> => {
    if (!data || busy) return;
    setBusy(true); setProblem("");
    try {
      const call = { start: api.startFeedback, "retry-filing": api.retryFeedbackFiling, "retry-run": api.retryFeedbackRun, close: api.closeFeedback }[action];
      record.set(await call(id, { expectedRevision: data.revision }));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
      if (error instanceof ApiError && error.status === 409) record.reload();
    } finally { setBusy(false); }
  };
  const addComment = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!data?.capabilities.comment || busy) return;
    const text = comment.trim();
    if (!text) { setProblem("Add a comment."); return; }
    setBusy(true); setProblem("");
    try {
      const result = await api.commentOnFeedback(id, { clientCommentId: commentId, comment: text, steer });
      record.set(result.request);
      setComment(""); setSteer(false); setCommentId(crypto.randomUUID());
    } catch (error) { setProblem(error instanceof Error ? error.message : String(error)); }
    finally { setBusy(false); }
  };
  if (record.error && !data && record.error instanceof ApiError && record.error.status === 404) {
    return <><Crumb to="/requests">Requests</Crumb><section className="card notyours"><span className="ny-ic"><Icon name="lock" size={20} /></span>
      <div><h1>Request unavailable</h1><Link className="btn" to="/requests">Requests</Link></div></section></>;
  }
  if (!data) return <><Crumb to="/requests">Requests</Crumb>{record.error ? <Callout kind="stop" icon="triangle-alert">{record.error.message} <button className="link" type="button" onClick={record.reload}>Try again</button></Callout> : <section className="card"><p className="empty" role="status">Loading request.</p></section>}</>;
  const status = STATUS[data.status];
  const latestReply = [...data.attempts].reverse().find((attempt) => attempt.reply)?.reply;
  const pr = [...data.attempts].reverse().find((attempt) => attempt.pullRequest)?.pullRequest;
  const names = new Map<string, string>(me.dealer ? [[me.dealer.id, me.dealer.name]] : []);
  return <>
    <Crumb to="/requests">Requests</Crumb>
    <Head title={requestTitle(data.comment)} sub={<>{data.id} · {scopeName(data.scope, names)} · {data.createdBy.name}</>}
      actions={<><StatusChip status={data.status} />{data.capabilities.close ? <button className="btn danger" type="button" disabled={busy} onClick={() => { void command("close"); }}>Close request</button> : null}</>} />
    <Callout kind={status.kind === "stop" ? "stop" : status.kind === "ok" ? "ok" : status.kind === "warn" ? "warn" : undefined} icon={status.kind === "stop" ? "triangle-alert" : status.kind === "ok" ? "check" : "clock"}>
      {status.text}
    </Callout>
    {problem || record.error ? <p role="alert" className="form-error request-problem">{problem || record.error?.message}</p> : null}
    <div className="request-layout">
      <div className="stack">
        <section className="card"><div className="card-h"><h2>Request</h2></div>
          <p className="request-text">{data.comment}</p>
          <dl className="request-facts"><div><dt>Submitted by</dt><dd>{data.createdBy.name}</dd></div><div><dt>Scope</dt><dd>{scopeName(data.scope, names)}</dd></div>
            {data.captures.length > 1 ? <div><dt>Screens</dt><dd>{data.captures.length}</dd></div> : <div><dt>Page</dt><dd>{data.captures[0]?.page.path ?? data.page.path}</dd></div>}
            <div><dt>Round</dt><dd>{data.round}</dd></div></dl>
          {data.captures.length ? <Screens id={id} captures={data.captures} /> : null}
        </section>
        {latestReply ? <section className="card"><div className="card-h"><h2>Reply</h2></div><p className="request-text">{latestReply}</p></section> : null}
        {pr ? <section className="card"><div className="card-h"><h2>Draft PR</h2></div><a href={pr.url} rel="noopener noreferrer" target="_blank">{pr.url}</a>
          <p className="scope-note">{data.status === "live" ? "Live, verified" : data.status === "merged" ? "Merged" : "Review required before merge"}</p></section> : null}
        <section className="card"><div className="card-h"><h2>Comments and follow-ups</h2></div>
          {data.comments.length ? data.comments.map((entry) => <div className="request-comment" key={entry.id}><b>{entry.author.name}</b><p>{entry.comment}</p>
            {entry.steer ? <span className="queued">{entry.round === null ? "Follow-up queued until active work ends" : "Follow-up requested"}</span> : null}</div>) : <p className="scope-note">No comments yet.</p>}
          {data.capabilities.comment ? <form className="request-comment-form" onSubmit={(event) => { void addComment(event); }}>
            <label className="field"><span>Comment</span><textarea className="input" value={comment} maxLength={4000} placeholder="Add a comment" onChange={(event) => { setComment(event.target.value); setCommentId(crypto.randomUUID()); }} /></label>
            <div className="request-comment-controls">{data.capabilities.steer ? <label className="checkline"><input type="checkbox" checked={steer} onChange={(event) => { setSteer(event.target.checked); setCommentId(crypto.randomUUID()); }} />Request another round</label> : null}
              <button className="btn" type="submit" disabled={busy}>Add comment</button></div>
          </form> : null}
        </section>
      </div>
      <aside className="stack"><section className="card"><div className="card-h"><h2>Activity</h2></div>
        {data.events.length ? <ol className="request-timeline">{[...data.events].reverse().map((entry, index) => <li key={entry.id} className={index === 0 ? "now" : ""}>
          <span className="request-dot" /><div><b>{entry.message}</b><span>{when(entry.at)}</span></div></li>)}</ol> : null}
      </section><section className="card"><div className="card-h"><h2>Agent work</h2></div>
          {data.capabilities.start ? <button className="btn primary" type="button" disabled={busy} onClick={() => { void command("start"); }}>Start agent work</button> : null}
          {data.capabilities.retryFiling ? <button className="btn primary" type="button" disabled={busy} onClick={() => { void command("retry-filing"); }}>Retry filing</button> : null}
          {data.capabilities.retryRun ? <button className="btn primary" type="button" disabled={busy} onClick={() => { void command("retry-run"); }}>Retry agent</button> : null}
          {!data.capabilities.start && !data.capabilities.retryFiling && !data.capabilities.retryRun ? <p className="scope-note">{data.status === "closed" ? "Closed." : "Phoneware staff start agent work."}</p> : null}
          <p className="scope-note">Agents cannot merge, deploy, or change tenant data.</p>
          {data.attempts.length ? <ol className="request-attempts">{[...data.attempts].reverse().map((attempt) => <li key={`${attempt.round}:${attempt.attempt}`}>
            <b>Round {attempt.round} · {STATUS[attempt.status].label}</b>
            {attempt.progress ? <span>{attempt.progress.message}</span> : null}
            {attempt.error ? <span className="form-error">{attempt.error}</span> : null}
          </li>)}</ol> : null}
        </section></aside>
    </div>
  </>;
}
